import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as Types from '@/types'
import { apiClient } from '@/services/api'

interface OperationsState {
  dailyReports: {
    items: Types.DailyReport[]
    current: Types.DailyReport | null
    total: number
    page: number
    pageSize: number
    filters: {
      operation_id?: string
    }
    isLoading: boolean
    error: string | null
  }
  // Enhanced daily report with activities (Sprint 3)
  dailyReportDetail: {
    report: Types.OperationDailyReportDetail | null
    activities: Types.OperationActivity[]
    isLoading: boolean
    error: string | null
  }
  // Activity codes lookup (Sprint 3)
  activityCodes: {
    items: Types.ActivityCode[]
    byCategory: Record<string, Record<string, Types.ActivityCode[]>>
    total: number
    page: number
    pageSize: number
    filters: {
      major_category?: string
      is_active?: boolean
      search?: string
    }
    isLoading: boolean
    error: string | null
  }
  // Operation activities (Sprint 3)
  operationActivities: {
    items: Types.OperationActivity[]
    total: number
    page: number
    pageSize: number
    filters: {
      operation_id?: string
      daily_report_id?: string
      major_category?: string
    }
    isLoading: boolean
    error: string | null
  }
  timeLogs: {
    items: Types.ActivityTimeLog[]
    total: number
    page: number
    pageSize: number
    filters: {
      operation_id?: string
      activity_type?: string
    }
    isLoading: boolean
    error: string | null
  }
  activitySummary: {
    data: Types.ActivitySummary | null
    isLoading: boolean
    error: string | null
  }
  risReports: {
    items: Types.RISReport[]
    current: Types.RISReport | null
    total: number
    page: number
    pageSize: number
    filters: {
      operation_id?: string
      severity?: string
      status?: string
    }
    isLoading: boolean
    error: string | null
  }
}

const initialState: OperationsState = {
  dailyReports: {
    items: [],
    current: null,
    total: 0,
    page: 1,
    pageSize: 20,
    filters: {},
    isLoading: false,
    error: null,
  },
  dailyReportDetail: {
    report: null,
    activities: [],
    isLoading: false,
    error: null,
  },
  activityCodes: {
    items: [],
    byCategory: {},
    total: 0,
    page: 1,
    pageSize: 100,
    filters: { is_active: true },
    isLoading: false,
    error: null,
  },
  operationActivities: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 50,
    filters: {},
    isLoading: false,
    error: null,
  },
  timeLogs: {
    items: [],
    total: 0,
    page: 1,
    pageSize: 20,
    filters: {},
    isLoading: false,
    error: null,
  },
  activitySummary: {
    data: null,
    isLoading: false,
    error: null,
  },
  risReports: {
    items: [],
    current: null,
    total: 0,
    page: 1,
    pageSize: 20,
    filters: {},
    isLoading: false,
    error: null,
  },
}

// Daily Report async thunks
export const fetchDailyReports = createAsyncThunk(
  'operations/fetchDailyReports',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.listDailyReports({
        skip,
        limit,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch daily reports')
    }
  }
)

export const fetchDailyReport = createAsyncThunk(
  'operations/fetchDailyReport',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getDailyReport(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch daily report')
    }
  }
)

export const createDailyReport = createAsyncThunk(
  'operations/createDailyReport',
  async (request: Partial<Types.DailyReport>, { rejectWithValue }) => {
    try {
      const response = await apiClient.createDailyReport(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create daily report')
    }
  }
)

// Time Log async thunks
export const fetchTimeLogs = createAsyncThunk(
  'operations/fetchTimeLogs',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.listTimeLogs({
        skip,
        limit,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch time logs')
    }
  }
)

export const createTimeLog = createAsyncThunk(
  'operations/createTimeLog',
  async (request: Partial<Types.ActivityTimeLog>, { rejectWithValue }) => {
    try {
      const response = await apiClient.createTimeLog(request)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create time log')
    }
  }
)

// Activity Summary async thunk
export const fetchActivitySummary = createAsyncThunk(
  'operations/fetchActivitySummary',
  async (operationId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getActivitySummary(operationId)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch activity summary')
    }
  }
)

// RIS Report async thunks
export const fetchRISReports = createAsyncThunk(
  'operations/fetchRISReports',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 20) : 0
      const response = await apiClient.listRISReports({
        skip,
        limit,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch RIS reports')
    }
  }
)

export const fetchRISReport = createAsyncThunk(
  'operations/fetchRISReport',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getRISReport(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch RIS report')
    }
  }
)

export const createRISReport = createAsyncThunk(
  'operations/createRISReport',
  async (request: Partial<Types.RISReport>, { rejectWithValue }) => {
    try {
      const response = await apiClient.createRISReport(request as Types.RISReportCreateRequest)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create RIS report')
    }
  }
)

export const updateRISReport = createAsyncThunk(
  'operations/updateRISReport',
  async ({ id, updates }: { id: string; updates: Partial<Types.RISReport> }, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateRISReport(id, updates)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update RIS report')
    }
  }
)

// ============================================================================
// Activity Code async thunks (Sprint 3)
// ============================================================================

export const fetchActivityCodes = createAsyncThunk(
  'operations/fetchActivityCodes',
  async (
    { page, limit, filters }: { page?: number; limit?: number; filters?: Record<string, any> } = {},
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 100) : 0
      const response = await apiClient.listActivityCodes({
        skip,
        limit: limit || 100,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch activity codes')
    }
  }
)

export const fetchActivityCodesByCategory = createAsyncThunk(
  'operations/fetchActivityCodesByCategory',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.listActivityCodesByCategory()
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch activity codes by category')
    }
  }
)

export const fetchActivityCode = createAsyncThunk(
  'operations/fetchActivityCode',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.getActivityCode(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch activity code')
    }
  }
)

// ============================================================================
// Operation Activity async thunks (Sprint 3)
// ============================================================================

export const fetchDailyReportWithActivities = createAsyncThunk(
  'operations/fetchDailyReportWithActivities',
  async ({ operationId, reportId }: { operationId: string; reportId: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.getDailyReportWithActivities(operationId, reportId)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch daily report with activities')
    }
  }
)

export const fetchOperationActivities = createAsyncThunk(
  'operations/fetchOperationActivities',
  async (
    {
      operationId,
      reportId,
      page,
      limit,
      filters,
    }: {
      operationId: string
      reportId: string
      page?: number
      limit?: number
      filters?: Record<string, any>
    },
    { rejectWithValue }
  ) => {
    try {
      const skip = page ? (page - 1) * (limit || 50) : 0
      const response = await apiClient.listOperationActivities(operationId, reportId, {
        skip,
        limit: limit || 50,
        ...filters,
      })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch operation activities')
    }
  }
)

export const createOperationActivity = createAsyncThunk(
  'operations/createOperationActivity',
  async (
    {
      operationId,
      reportId,
      data,
    }: { operationId: string; reportId: string; data: Types.OperationActivityCreateRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.createOperationActivity(operationId, reportId, data)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create operation activity')
    }
  }
)

export const updateOperationActivity = createAsyncThunk(
  'operations/updateOperationActivity',
  async (
    {
      operationId,
      reportId,
      activityId,
      data,
    }: {
      operationId: string
      reportId: string
      activityId: string
      data: Types.OperationActivityUpdateRequest
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.updateOperationActivity(operationId, reportId, activityId, data)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update operation activity')
    }
  }
)

export const deleteOperationActivity = createAsyncThunk(
  'operations/deleteOperationActivity',
  async (
    { operationId, reportId, activityId }: { operationId: string; reportId: string; activityId: string },
    { rejectWithValue }
  ) => {
    try {
      await apiClient.deleteOperationActivity(operationId, reportId, activityId)
      return activityId // Return the deleted activity ID for state cleanup
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete operation activity')
    }
  }
)

export const reorderOperationActivities = createAsyncThunk(
  'operations/reorderOperationActivities',
  async (
    { operationId, reportId, activityIds }: { operationId: string; reportId: string; activityIds: string[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.reorderOperationActivities(operationId, reportId, { activity_ids: activityIds })
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to reorder operation activities')
    }
  }
)

const operationsSlice = createSlice({
  name: 'operations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.dailyReports.error = null
      state.timeLogs.error = null
      state.activitySummary.error = null
      state.activityCodes.error = null
      state.operationActivities.error = null
      state.dailyReportDetail.error = null
    },
    setDailyReportFilters: (state, action) => {
      state.dailyReports.filters = action.payload
      state.dailyReports.page = 1
    },
    setTimeLogFilters: (state, action) => {
      state.timeLogs.filters = action.payload
      state.timeLogs.page = 1
    },
    setRISReportFilters: (state, action) => {
      state.risReports.filters = action.payload
      state.risReports.page = 1
    },
    // Activity codes reducers (Sprint 3)
    setActivityCodeFilters: (state, action) => {
      state.activityCodes.filters = action.payload
      state.activityCodes.page = 1
    },
    // Operation activities reducers (Sprint 3)
    setOperationActivityFilters: (state, action) => {
      state.operationActivities.filters = action.payload
      state.operationActivities.page = 1
    },
    // Daily report detail reducer (Sprint 3)
    clearDailyReportDetail: (state) => {
      state.dailyReportDetail.report = null
      state.dailyReportDetail.activities = []
      state.dailyReportDetail.error = null
    },
  },
  extraReducers: (builder) => {
    // Daily Reports
    builder
      .addCase(fetchDailyReports.pending, (state) => {
        state.dailyReports.isLoading = true
        state.dailyReports.error = null
      })
      .addCase(fetchDailyReports.fulfilled, (state, action) => {
        state.dailyReports.isLoading = false
        state.dailyReports.items = action.payload.items
        state.dailyReports.total = action.payload.total
        state.dailyReports.page = action.payload.page
        state.dailyReports.pageSize = action.payload.page_size
      })
      .addCase(fetchDailyReports.rejected, (state, action) => {
        state.dailyReports.isLoading = false
        state.dailyReports.error = action.payload as string
      })

    builder
      .addCase(fetchDailyReport.pending, (state) => {
        state.dailyReports.isLoading = true
        state.dailyReports.error = null
      })
      .addCase(fetchDailyReport.fulfilled, (state, action) => {
        state.dailyReports.isLoading = false
        state.dailyReports.current = action.payload
      })
      .addCase(fetchDailyReport.rejected, (state, action) => {
        state.dailyReports.isLoading = false
        state.dailyReports.error = action.payload as string
      })

    builder
      .addCase(createDailyReport.pending, (state) => {
        state.dailyReports.isLoading = true
        state.dailyReports.error = null
      })
      .addCase(createDailyReport.fulfilled, (state, action) => {
        state.dailyReports.isLoading = false
        state.dailyReports.items.unshift(action.payload)
        state.dailyReports.current = action.payload
      })
      .addCase(createDailyReport.rejected, (state, action) => {
        state.dailyReports.isLoading = false
        state.dailyReports.error = action.payload as string
      })

    // Time Logs
    builder
      .addCase(fetchTimeLogs.pending, (state) => {
        state.timeLogs.isLoading = true
        state.timeLogs.error = null
      })
      .addCase(fetchTimeLogs.fulfilled, (state, action) => {
        state.timeLogs.isLoading = false
        state.timeLogs.items = action.payload.items
        state.timeLogs.total = action.payload.total
        state.timeLogs.page = action.payload.page
        state.timeLogs.pageSize = action.payload.page_size
      })
      .addCase(fetchTimeLogs.rejected, (state, action) => {
        state.timeLogs.isLoading = false
        state.timeLogs.error = action.payload as string
      })

    builder
      .addCase(createTimeLog.pending, (state) => {
        state.timeLogs.isLoading = true
        state.timeLogs.error = null
      })
      .addCase(createTimeLog.fulfilled, (state, action) => {
        state.timeLogs.isLoading = false
        state.timeLogs.items.unshift(action.payload)
      })
      .addCase(createTimeLog.rejected, (state, action) => {
        state.timeLogs.isLoading = false
        state.timeLogs.error = action.payload as string
      })

    // Activity Summary
    builder
      .addCase(fetchActivitySummary.pending, (state) => {
        state.activitySummary.isLoading = true
        state.activitySummary.error = null
      })
      .addCase(fetchActivitySummary.fulfilled, (state, action) => {
        state.activitySummary.isLoading = false
        state.activitySummary.data = action.payload
      })
      .addCase(fetchActivitySummary.rejected, (state, action) => {
        state.activitySummary.isLoading = false
        state.activitySummary.error = action.payload as string
      })

    // RIS Reports
    builder
      .addCase(fetchRISReports.pending, (state) => {
        state.risReports.isLoading = true
        state.risReports.error = null
      })
      .addCase(fetchRISReports.fulfilled, (state, action) => {
        state.risReports.isLoading = false
        state.risReports.items = action.payload.items
        state.risReports.total = action.payload.total
        state.risReports.page = action.payload.page
        state.risReports.pageSize = action.payload.page_size
      })
      .addCase(fetchRISReports.rejected, (state, action) => {
        state.risReports.isLoading = false
        state.risReports.error = action.payload as string
      })

    builder
      .addCase(fetchRISReport.pending, (state) => {
        state.risReports.isLoading = true
        state.risReports.error = null
      })
      .addCase(fetchRISReport.fulfilled, (state, action) => {
        state.risReports.isLoading = false
        state.risReports.current = action.payload
      })
      .addCase(fetchRISReport.rejected, (state, action) => {
        state.risReports.isLoading = false
        state.risReports.error = action.payload as string
      })

    builder
      .addCase(createRISReport.pending, (state) => {
        state.risReports.isLoading = true
        state.risReports.error = null
      })
      .addCase(createRISReport.fulfilled, (state, action) => {
        state.risReports.isLoading = false
        state.risReports.items.unshift(action.payload)
        state.risReports.current = action.payload
      })
      .addCase(createRISReport.rejected, (state, action) => {
        state.risReports.isLoading = false
        state.risReports.error = action.payload as string
      })

    builder
      .addCase(updateRISReport.pending, (state) => {
        state.risReports.isLoading = true
        state.risReports.error = null
      })
      .addCase(updateRISReport.fulfilled, (state, action) => {
        state.risReports.isLoading = false
        const index = state.risReports.items.findIndex((item) => item.id === action.payload.id)
        if (index !== -1) {
          state.risReports.items[index] = action.payload
        }
        state.risReports.current = action.payload
      })
      .addCase(updateRISReport.rejected, (state, action) => {
        state.risReports.isLoading = false
        state.risReports.error = action.payload as string
      })

    // ============================================================================
    // Activity Code Handlers (Sprint 3)
    // ============================================================================

    builder
      .addCase(fetchActivityCodes.pending, (state) => {
        state.activityCodes.isLoading = true
        state.activityCodes.error = null
      })
      .addCase(fetchActivityCodes.fulfilled, (state, action) => {
        state.activityCodes.isLoading = false
        state.activityCodes.items = action.payload.items
        state.activityCodes.total = action.payload.total
        state.activityCodes.page = action.payload.page
        state.activityCodes.pageSize = action.payload.page_size
      })
      .addCase(fetchActivityCodes.rejected, (state, action) => {
        state.activityCodes.isLoading = false
        state.activityCodes.error = action.payload as string
      })

    builder
      .addCase(fetchActivityCodesByCategory.pending, (state) => {
        state.activityCodes.isLoading = true
        state.activityCodes.error = null
      })
      .addCase(fetchActivityCodesByCategory.fulfilled, (state, action) => {
        state.activityCodes.isLoading = false
        state.activityCodes.byCategory = action.payload
      })
      .addCase(fetchActivityCodesByCategory.rejected, (state, action) => {
        state.activityCodes.isLoading = false
        state.activityCodes.error = action.payload as string
      })

    builder
      .addCase(fetchActivityCode.pending, (state) => {
        state.activityCodes.isLoading = true
        state.activityCodes.error = null
      })
      .addCase(fetchActivityCode.fulfilled, (state, action) => {
        state.activityCodes.isLoading = false
        // Update in list if exists, otherwise add
        const index = state.activityCodes.items.findIndex((item) => item.id === action.payload.id)
        if (index !== -1) {
          state.activityCodes.items[index] = action.payload
        } else {
          state.activityCodes.items.push(action.payload)
        }
      })
      .addCase(fetchActivityCode.rejected, (state, action) => {
        state.activityCodes.isLoading = false
        state.activityCodes.error = action.payload as string
      })

    // ============================================================================
    // Daily Report Detail Handlers (Sprint 3)
    // ============================================================================

    builder
      .addCase(fetchDailyReportWithActivities.pending, (state) => {
        state.dailyReportDetail.isLoading = true
        state.dailyReportDetail.error = null
      })
      .addCase(fetchDailyReportWithActivities.fulfilled, (state, action) => {
        state.dailyReportDetail.isLoading = false
        state.dailyReportDetail.report = action.payload
        state.dailyReportDetail.activities = action.payload.activities || []
      })
      .addCase(fetchDailyReportWithActivities.rejected, (state, action) => {
        state.dailyReportDetail.isLoading = false
        state.dailyReportDetail.error = action.payload as string
      })

    // ============================================================================
    // Operation Activity Handlers (Sprint 3)
    // ============================================================================

    builder
      .addCase(fetchOperationActivities.pending, (state) => {
        state.operationActivities.isLoading = true
        state.operationActivities.error = null
      })
      .addCase(fetchOperationActivities.fulfilled, (state, action) => {
        state.operationActivities.isLoading = false
        state.operationActivities.items = action.payload.items
        state.operationActivities.total = action.payload.total
        state.operationActivities.page = action.payload.page
        state.operationActivities.pageSize = action.payload.page_size
      })
      .addCase(fetchOperationActivities.rejected, (state, action) => {
        state.operationActivities.isLoading = false
        state.operationActivities.error = action.payload as string
      })

    builder
      .addCase(createOperationActivity.pending, (state) => {
        state.operationActivities.isLoading = true
        state.operationActivities.error = null
        state.dailyReportDetail.isLoading = true
      })
      .addCase(createOperationActivity.fulfilled, (state, action) => {
        state.operationActivities.isLoading = false
        state.dailyReportDetail.isLoading = false
        // Add to activities list (sorted by start_time happens on API)
        state.operationActivities.items.push(action.payload)
        // Also add to daily report detail activities
        if (state.dailyReportDetail.activities) {
          state.dailyReportDetail.activities.push(action.payload)
          // Sort by start_time
          state.dailyReportDetail.activities.sort(
            (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
          )
        }
      })
      .addCase(createOperationActivity.rejected, (state, action) => {
        state.operationActivities.isLoading = false
        state.dailyReportDetail.isLoading = false
        state.operationActivities.error = action.payload as string
      })

    builder
      .addCase(updateOperationActivity.pending, (state) => {
        state.operationActivities.isLoading = true
        state.operationActivities.error = null
        state.dailyReportDetail.isLoading = true
      })
      .addCase(updateOperationActivity.fulfilled, (state, action) => {
        state.operationActivities.isLoading = false
        state.dailyReportDetail.isLoading = false
        // Update in list
        const index = state.operationActivities.items.findIndex((item) => item.id === action.payload.id)
        if (index !== -1) {
          state.operationActivities.items[index] = action.payload
        }
        // Update in daily report detail
        const detailIndex = state.dailyReportDetail.activities?.findIndex(
          (item) => item.id === action.payload.id
        )
        if (detailIndex !== undefined && detailIndex !== -1 && state.dailyReportDetail.activities) {
          state.dailyReportDetail.activities[detailIndex] = action.payload
          // Re-sort by start_time
          state.dailyReportDetail.activities.sort(
            (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
          )
        }
      })
      .addCase(updateOperationActivity.rejected, (state, action) => {
        state.operationActivities.isLoading = false
        state.dailyReportDetail.isLoading = false
        state.operationActivities.error = action.payload as string
      })

    builder
      .addCase(deleteOperationActivity.pending, (state) => {
        state.operationActivities.isLoading = true
        state.operationActivities.error = null
        state.dailyReportDetail.isLoading = true
      })
      .addCase(deleteOperationActivity.fulfilled, (state, action) => {
        state.operationActivities.isLoading = false
        state.dailyReportDetail.isLoading = false
        // Remove from list
        state.operationActivities.items = state.operationActivities.items.filter(
          (item) => item.id !== action.payload
        )
        // Remove from daily report detail
        if (state.dailyReportDetail.activities) {
          state.dailyReportDetail.activities = state.dailyReportDetail.activities.filter(
            (item) => item.id !== action.payload
          )
        }
      })
      .addCase(deleteOperationActivity.rejected, (state, action) => {
        state.operationActivities.isLoading = false
        state.dailyReportDetail.isLoading = false
        state.operationActivities.error = action.payload as string
      })

    builder
      .addCase(reorderOperationActivities.pending, (state) => {
        state.operationActivities.isLoading = true
        state.operationActivities.error = null
      })
      .addCase(reorderOperationActivities.fulfilled, (state, action) => {
        state.operationActivities.isLoading = false
        // Activities are sorted on API side, update our local arrays
        state.operationActivities.items = action.payload.items || state.operationActivities.items
        if (state.dailyReportDetail.activities) {
          state.dailyReportDetail.activities = action.payload.items || state.dailyReportDetail.activities
        }
      })
      .addCase(reorderOperationActivities.rejected, (state, action) => {
        state.operationActivities.isLoading = false
        state.operationActivities.error = action.payload as string
      })
  },
})

export const {
  clearError,
  setDailyReportFilters,
  setTimeLogFilters,
  setRISReportFilters,
  setActivityCodeFilters,
  setOperationActivityFilters,
  clearDailyReportDetail,
} = operationsSlice.actions
export default operationsSlice.reducer
